import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { heroHeadlines } from '@/mocks/articles';

export default function HeroSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroHeadlines.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const article = heroHeadlines[current];

  return (
    <section className="relative w-full min-h-[580px] md:min-h-[680px] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700"
        style={{ backgroundImage: `url(${article.coverImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-accent-900/70 via-accent-900/50 to-background-50"></div>
      </div>

      <div className="relative z-10 w-full h-full">
        <div className="w-full px-4 md:px-6 pt-32 md:pt-40 pb-16 md:pb-20">
          <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-20">
            <div className="w-full lg:w-[55%]">
              <span className="inline-block text-xs font-medium text-primary-500 bg-primary-500/10 px-3 py-1 rounded-full mb-6">
                {t('home:hero.hotTag')}
              </span>
              <div className={`transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-background-50 leading-tight mb-6">
                  {t('home:hero.line1')}
                  <br />
                  {t('home:hero.line2')}
                  <br />
                  {t('home:hero.line3')}
                </h1>
              </div>
            </div>

            <div className="w-full lg:w-[40%] lg:pt-8">
              <div className={`transition-all duration-500 delay-150 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                <p className="text-base md:text-lg text-background-50/80 leading-relaxed mb-6">
                  {t('home:hero.subtitle')}
                </p>
                <Link
                  to={`/article/${article.slug}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap border border-background-50/40 text-background-50 hover:bg-background-50 hover:text-accent-900 px-6 py-3 rounded-full text-sm font-medium cursor-pointer transition-all duration-300"
                >
                  {t('home:hero.viewMore')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className={`mt-12 md:mt-16 transition-all duration-500 delay-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
            <Link
              to={`/article/${article.slug}`}
              className="block w-full cursor-pointer group"
            >
              <div className="bg-background-50/10 backdrop-blur-sm border border-background-50/20 rounded-xl p-5 md:p-6 hover:bg-background-50/15 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <span className="shrink-0 text-xs font-medium text-primary-500 bg-primary-500/15 px-2 py-0.5 rounded mt-0.5 whitespace-nowrap">
                    {t('home:hero.latest')}
                  </span>
                  <div>
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-background-50 mb-2 group-hover:text-primary-300 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-sm text-background-50/60 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-background-50/50">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.viewCount.toLocaleString()} {t('home:articles.views')}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-message-2-line"></i>
                        {article.commentCount.toLocaleString()} {t('home:articles.comments')}
                      </span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <div className="flex items-center gap-3 mt-8">
            {heroHeadlines.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                className={`w-2 h-2 rounded-full cursor-pointer transition-all duration-300 ${
                  idx === current
                    ? 'bg-primary-500 w-6'
                    : 'bg-background-50/40 hover:bg-background-50/60'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}